import { Banner, DataTable, EmptyState, KeyValueList, Select, Skeleton, type Column } from "@omony/ui";
import { useQuery } from "@tanstack/react-query";
import { Receipt } from "lucide-react";
import { useState } from "react";
import { useIntl } from "react-intl";
import { api, errorMessageId } from "../../shared/api.js";
import { EMPTY, fmtDate } from "../../shared/format.js";
import { euro, StepFrame, useT } from "../flow/common.js";

interface Item {
  id: string;
  reference: string;
  date: string;
  status: string;
  code: string;
  amountCents: number;
  billingStatus: "offen" | "eingereicht" | "entfaellt";
  insuranceType: "gkv" | "pkv";
  label: { initials: string; age: number | null };
  patient: { vorname: string; name: string; kvnr: string; krankenkasse: string } | null;
}
interface Month {
  month: string;
  items: Item[];
  totals: { count: number; amountCents: number; openCents: number; submittedCents: number };
}

const monthKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

/** Monatsübersicht der Abrechnung (F7) für das Apotheken-Konto */
export function BillingOverview() {
  const t = useT();
  const intl = useIntl();
  const months = Array.from({ length: 12 }, (_, i) => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - i);
    return { id: monthKey(d), label: intl.formatDate(d, { month: "long", year: "numeric" }) };
  });
  const [month, setMonth] = useState(months[0]!.id);
  const q = useQuery({ queryKey: ["billing", month], queryFn: () => api<Month>(`/billing?month=${month}`) });
  const cols: Column<Item>[] = [
    { key: "date", header: t("console.billing.date"), cell: (r) => fmtDate(intl, r.date) },
    { key: "ref", header: t("console.billing.reference"), cell: (r) => <bdi dir="ltr">{r.reference}</bdi> },
    {
      key: "patient",
      header: t("console.billing.patient"),
      cell: (r) =>
        r.patient ? (
          <bdi>{t("console.billing.patientName", { name: r.patient.name, vorname: r.patient.vorname })}</bdi>
        ) : (
          r.label.initials || EMPTY
        ),
    },
    {
      key: "kvnr",
      header: t("console.patient.field.kvnr"),
      cell: (r) => <bdi dir="ltr">{r.patient?.kvnr ?? EMPTY}</bdi>,
    },
    {
      key: "ins",
      header: t("console.patient.insurance"),
      cell: (r) => t(`console.patient.${r.insuranceType}`),
    },
    { key: "code", header: t("console.billing.code"), cell: (r) => <bdi dir="ltr">{r.code}</bdi> },
    {
      key: "state",
      header: t("admin.status.label"),
      cell: (r) => t(`console.billing.status.${r.billingStatus}`),
    },
    {
      key: "amount",
      header: t("console.billing.amount"),
      align: "end",
      cell: (r) => euro(r.amountCents, intl.locale),
    },
  ];
  return (
    <StepFrame title={t("console.billing.title")} lead={t("console.billing.overviewLead")}>
      <Select label={t("console.billing.month")} value={month} onChange={setMonth} options={months} />
      {q.isLoading ? (
        <Skeleton lines={5} />
      ) : q.error ? (
        <Banner tone="danger">{t(errorMessageId(q.error))}</Banner>
      ) : q.data ? (
        <>
          <KeyValueList
            items={[
              { key: "count", label: t("console.billing.count"), value: String(q.data.totals.count) },
              {
                key: "sum",
                label: t("console.billing.sum"),
                value: euro(q.data.totals.amountCents, intl.locale),
              },
              {
                key: "open",
                label: t("console.billing.status.offen"),
                value: euro(q.data.totals.openCents, intl.locale),
              },
              {
                key: "sub",
                label: t("console.billing.status.eingereicht"),
                value: euro(q.data.totals.submittedCents, intl.locale),
              },
            ]}
          />
          <DataTable
            caption={t("console.billing.title")}
            columns={cols}
            rows={q.data.items}
            rowKey={(r) => r.id}
            empty={<EmptyState icon={<Receipt size={22} />} title={t("console.billing.empty")} />}
          />
        </>
      ) : null}
    </StepFrame>
  );
}
